import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Spinner } from 'reactstrap';
import { Btn, H4, H5, H6, P } from '../../../AbstractElements';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Users, 
  FileText, 
  DollarSign, 
  Bell, 
  Plus,
  Calendar,
  CheckCircle,
  Clock,
  XCircle
} from 'react-feather';
import LegalConsultationService from '../../../Api/services/legal-consultation.service';
import NotificationService from '../../../Api/services/notification.service';
import DashboardService from '../../../Api/services/dashboard.service';
import { toast } from 'react-toastify';
import { RoleGuard } from '../../../Components/Auth';
import type { components } from '../../../Api/types/openapi';

type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];
type NotificationResponse = components['schemas']['NotificationResponse'];

interface DashboardMetrics {
  total: number;
  pending: number;
  complete: number;
  pendingPayment: number;
}

const CustomDashboard = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    total: 0,
    pending: 0,
    complete: 0,
    pendingPayment: 0,
  });
  const [consultations, setConsultations] = useState<LegalConsulationResponse[]>([]);
  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const promises = [];

      // Cargar métricas del dashboard
      promises.push(
        DashboardService.getAllMetrics()
          .catch((error: any) => {
            // Manejar errores silenciosamente - si falla, usar valores por defecto
            if (error.response?.status === 403 || error.response?.status === 404) {
              return { total: 0, pending: 0, complete: 0, pendingPayment: 0 };
            }
            console.warn('Error loading dashboard metrics:', error);
            return { total: 0, pending: 0, complete: 0, pendingPayment: 0 };
          })
      );

      // Verificar el rol del usuario antes de hacer llamadas
      const userRole = user.role?.name?.toLowerCase() || '';
      const isAdmin = hasRole('admin');
      const isLawyer = hasRole('lawyer');
      const isClient = hasRole('client');

      // Debug: mostrar el rol del usuario
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Dashboard - Rol del usuario:', {
          roleName: user.role?.name,
          normalizedRole: userRole,
          isAdmin,
          isLawyer,
          isClient,
        });
      }

      // Cargar consultas según el rol
      // Intentar según el rol detectado, pero manejar 403 silenciosamente
      // El backend tiene la última palabra sobre permisos
      if (isAdmin) {
        promises.push(
          LegalConsultationService.getAdminConsultations({ limit: 5 })
            .catch((error: any) => {
              // Manejar 403 silenciosamente - el usuario simplemente no tiene permisos
              // Esto es normal y esperado, no es un error
              if (error.response?.status === 403) {
                // Silencioso - no loggear en producción
                return null;
              }
              throw error;
            })
        );
      } else if (isLawyer) {
        promises.push(
          LegalConsultationService.getLawyerConsultations({ limit: 5 })
            .catch((error: any) => {
              if (error.response?.status === 403) {
                return null;
              }
              throw error;
            })
        );
      } else if (isClient) {
        promises.push(
          LegalConsultationService.getClientConsultations({ limit: 5 })
            .catch((error: any) => {
              if (error.response?.status === 403) {
                return null;
              }
              throw error;
            })
        );
      } else {
        // Si no tiene ningún rol válido, no cargar consultas
        promises.push(Promise.resolve(null));
      }

      // Cargar notificaciones del menú
      promises.push(
        NotificationService.getMenuNotifications()
          .catch((error: any) => {
            // Manejar 403 silenciosamente
            if (error.response?.status === 403) {
              return null;
            }
            throw error;
          })
      );

      const results = await Promise.all(promises);
      
      // Procesar métricas (primer resultado)
      if (results[0]) {
        const metricsData = results[0] as DashboardMetrics;
        setMetrics(metricsData);
      } else {
        setMetrics({ total: 0, pending: 0, complete: 0, pendingPayment: 0 });
      }
      
      // Procesar consultas (vienen paginadas) - segundo resultado (índice 1)
      if (results[1]) {
        const consultationData = results[1] as any;
        // VALIDAR que la respuesta tenga results
        if (consultationData && consultationData.results && Array.isArray(consultationData.results)) {
          setConsultations(consultationData.results);
        } else if (Array.isArray(consultationData)) {
          setConsultations(consultationData);
        } else {
          console.warn('Formato de respuesta de consultas inesperado:', consultationData);
          setConsultations([]);
        }
      } else {
        setConsultations([]);
      }
      
      // Procesar notificaciones - último resultado
      if (results[results.length - 1]) {
        const menuNotifications = results[results.length - 1] as any;
        // VALIDAR que la respuesta tenga results
        if (menuNotifications && Array.isArray(menuNotifications.results)) {
          setNotifications(menuNotifications.results);
        } else {
          setNotifications([]);
        }
        // VALIDAR que cant sea un número
        const unreadCount = menuNotifications?.cant;
        setUnreadNotifications(typeof unreadCount === 'number' ? unreadCount : 0);
      } else {
        setNotifications([]);
        setUnreadNotifications(0);
      }
    } catch (error: any) {
      // Solo loggear errores que no sean 403 (403 ya se manejan silenciosamente en los .catch individuales)
      // Los errores 403 son esperados cuando el usuario no tiene permisos y ya se manejan arriba
      if (error.response?.status !== 403) {
        console.error('Error loading dashboard data:', error);
        
        // Manejar errores específicos
        if (error.response?.status === 404) {
          // No mostrar error si es 404 (recurso no encontrado)
          // Solo loggear en desarrollo
          if (process.env.NODE_ENV === 'development') {
            console.warn('ℹ️ Recurso no encontrado');
          }
        } else if (error.response?.status && error.response.status >= 500) {
          // Solo mostrar error para errores del servidor
          const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar datos del dashboard';
          toast.error(errorMessage);
        } else if (error.response?.status) {
          // Otros errores HTTP (excepto 403 y 404)
          const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar datos del dashboard';
          toast.error(errorMessage);
        }
      }
      // Asegurar que los estados estén vacíos en caso de error
      setMetrics({ total: 0, pending: 0, complete: 0, pendingPayment: 0 });
      setConsultations([]);
      setNotifications([]);
      setUnreadNotifications(0);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <Container fluid>
        <Row>
          <Col sm="12">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <Spinner color="primary" />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* Métricas Modernizadas */}
      <Row className="mb-4">
        <Col md="3" sm="6" className="mb-3">
          <Card className="text-center shadow-sm border-0" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(102, 126, 234, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <CardBody className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <FileText size={40} className="text-white" style={{ opacity: 0.9 }} />
                <div className="text-end">
                  <H4 className="text-white mb-0" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {metrics.total.toLocaleString()}
                  </H4>
                </div>
              </div>
              <p className="text-white mb-0" style={{ fontSize: '0.9rem', opacity: 0.95, fontWeight: '500' }}>
                {t("Consultas Totales")}
              </p>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="text-center shadow-sm border-0" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(245, 87, 108, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <CardBody className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <Clock size={40} className="text-white" style={{ opacity: 0.9 }} />
                <div className="text-end">
                  <H4 className="text-white mb-0" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {metrics.pending.toLocaleString()}
                  </H4>
                </div>
              </div>
              <p className="text-white mb-0" style={{ fontSize: '0.9rem', opacity: 0.95, fontWeight: '500' }}>
                {t("Consultas Pendientes")}
              </p>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="text-center shadow-sm border-0" style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            borderRadius: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(79, 172, 254, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <CardBody className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <CheckCircle size={40} className="text-white" style={{ opacity: 0.9 }} />
                <div className="text-end">
                  <H4 className="text-white mb-0" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {metrics.complete.toLocaleString()}
                  </H4>
                </div>
              </div>
              <p className="text-white mb-0" style={{ fontSize: '0.9rem', opacity: 0.95, fontWeight: '500' }}>
                {t("Consultas Realizadas")}
              </p>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="text-center shadow-sm border-0" style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            borderRadius: '12px',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(250, 112, 154, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <CardBody className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <DollarSign size={40} className="text-white" style={{ opacity: 0.9 }} />
                <div className="text-end">
                  <H4 className="text-white mb-0" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {metrics.pendingPayment.toLocaleString()}
                  </H4>
                </div>
              </div>
              <p className="text-white mb-0" style={{ fontSize: '0.9rem', opacity: 0.95, fontWeight: '500' }}>
                {t("Pagos Pendientes")}
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Accesos Rápidos */}
      <Row className="mb-4">
        <Col sm="12">
          <Card>
            <CardHeader>
              <H5>{t("Accesos Rápidos")}</H5>
            </CardHeader>
            <CardBody>
              <Row>
                <RoleGuard allowedRoles="admin">
                  <Col md="3" sm="6" className="mb-3">
                    <Btn
                      color="light"
                      block
                      className="h-100 d-flex flex-column align-items-center justify-content-center p-3"
                      onClick={() => navigate(`${process.env.PUBLIC_URL}/users/management`)}
                    >
                      <Users size={24} className="mb-2" />
                      <span>{t("Gestión de Usuarios")}</span>
                    </Btn>
                  </Col>
                </RoleGuard>
                <Col md="3" sm="6" className="mb-3">
                  <Btn
                    color="light"
                    block
                    className="h-100 d-flex flex-column align-items-center justify-content-center p-3"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/legal-consultations`)}
                  >
                    <FileText size={24} className="mb-2" />
                    <span>{t("Consultas Legales")}</span>
                  </Btn>
                </Col>
                <Col md="3" sm="6" className="mb-3">
                  <Btn
                    color="light"
                    block
                    className="h-100 d-flex flex-column align-items-center justify-content-center p-3"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/payments`)}
                  >
                    <DollarSign size={24} className="mb-2" />
                    <span>{t("Pagos")}</span>
                  </Btn>
                </Col>
                <Col md="3" sm="6" className="mb-3">
                  <Btn
                    color="light"
                    block
                    className="h-100 d-flex flex-column align-items-center justify-content-center p-3"
                    onClick={() => navigate(`${process.env.PUBLIC_URL}/notifications`)}
                  >
                    <Bell size={24} className="mb-2" />
                    <span>
                      {t("Notificaciones")}
                      {unreadNotifications > 0 && (
                        <Badge color="danger" className="ms-2">{unreadNotifications}</Badge>
                      )}
                    </span>
                  </Btn>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Notificaciones Recientes */}
      {notifications.length > 0 && (
        <Row className="mt-4">
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <H5>
                  {t("Notificaciones Recientes")}
                  {unreadNotifications > 0 && (
                    <Badge color="danger" className="ms-2">{unreadNotifications} {t("sin leer")}</Badge>
                  )}
                </H5>
                <Btn
                  color="link"
                  onClick={() => navigate(`${process.env.PUBLIC_URL}/notifications`)}
                >
                  {t("Ver todas")}
                </Btn>
              </CardHeader>
              <CardBody>
                <div className="list-group">
                  {notifications.slice(0, 5).map((notification) => (
                    <div
                      key={notification.id}
                      className="list-group-item d-flex justify-content-between align-items-start"
                    >
                      <div className="ms-2 me-auto">
                        <div className="fw-bold">{notification.title || t("Sin título")}</div>
                        <small className="text-muted">{notification.description || ''}</small>
                      </div>
                      <small className="text-muted">
                        {notification.created_at
                          ? new Date(notification.created_at).toLocaleDateString()
                          : ''}
                      </small>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default CustomDashboard;

